<template>
  <div class="container pt-115 pb-85">
    <div class="row">
      <div class="col-xl-4 mb-4 mb-xl-0">
        <h6 class="fs-16 fw-normal text-para mb-12">{{ subtitle }}</h6>
        <h2 class="section-title mb-20">{{ title }}</h2>
        <router-link
          :to="viewAllLink"
          class="btn style-one d-inline-flex flex-wrap align-items-center transition"
        >
          <span
            class="btn-text d-flex flex-column align-items-center justify-content-center transition"
          >
            {{ viewAllText }}
          </span>
          <span
            class="btn-icon d-flex flex-column align-items-center justify-content-center transition"
          >
            <img :src="rightArrowIcon" alt="Icon" />
          </span>
        </router-link>
      </div>

      <div class="col-xl-8">
        <div class="row">
          <div 
            v-for="(post, index) in blogPosts" 
            :key="post.id || index" 
            class="col-md-6"
          >
            <div class="blog-card style-one img-hover-scale mb-30">
              <div class="blog-img position-relative img-scale overflow-hidden round-20 mb-10">
                <img 
                  :src="post.banner_image || post.featured_image" 
                  :alt="post.title" 
                  class="round-20 transition" 
                />
                <router-link
                  v-if="post.published_at || post.created_at"
                  :to="`/blog/${post.slug}`"
                  class="blog-date text-para d-flex flex-column align-items-center justify-content-center bg-linen text-center position-absolute round-10 transition z-1"
                >
                  <span class="d-block fw-semibold text-title">{{ formatDay(post.published_at || post.created_at) }}</span>
                  {{ formatMonth(post.published_at || post.created_at) }}
                </router-link>
                <router-link
                  :to="`/blog/${post.slug}`"
                  class="position-absolute top-0 start-0 w-100 h-100 z-0"
                ></router-link>
              </div>
              <div class="blog-info bg_white round-20">
                <ul class="blog-metainfo list-unstyled mb-6">
                  <li class="position-relative" v-if="post.author || post.author_name">
                    <router-link :to="`/blog/${post.slug}`">
                      By: {{ post.author || post.author_name }}
                    </router-link>
                  </li>
                  <li class="position-relative" v-if="post.comments_count !== undefined">
                    {{ post.comments_count }}
                    {{ post.comments_count === 1 ? 'Comment' : 'Comments' }}
                  </li>
                </ul>
                <h3 class="fs-20 fw-semibold mb-0">
                  <router-link
                    :to="`/blog/${post.slug}`"
                    class="text-title link-hover-primary transition"
                  >
                    {{ post.title }}
                  </router-link>
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import rightArrowIcon from '@/assets/img/icons/right-arrow.svg'

export default {
    name: 'Blog',
    data() {
        return {
            subtitle: 'Legal Insights Blog',
            title: 'Stay informed with the latest legal updates',
            viewAllText: 'View All Blog',
            viewAllLink: '/blog',
            blogPosts: [],
            rightArrowIcon
        }
    },
    async mounted() {
        await this.loadBlogPosts()
        await this.loadSectionSettings()
    },
    methods: {
        async loadBlogPosts() {
            try {
                const response = await this.$api.get('public/blogs')
                if (response.data && response.data.status === 'success' && response.data.data) {
                    // Get only the first 4 blog posts for the home page (2x2 grid)
                    this.blogPosts = response.data.data.slice(0, 4)
                }
            } catch (error) {
                console.error('Failed to load blog posts:', error)
                this.blogPosts = []
            }
        },
        
        async loadSectionSettings() {
            try {
                const response = await this.$api.get('public/settings?section=home')
                if (response.data && response.data.status === 'success') {
                    const data = response.data.data
                    
                    if (data.blog_subtitle) {
                        this.subtitle = data.blog_subtitle
                    }
                    if (data.blog_title) {
                        this.title = data.blog_title
                    }
                }
            } catch (error) {
                console.error('Failed to load section settings:', error)
            }
        },
        
        formatDay(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date.getDate().toString().padStart(2, '0')
        },
        
        formatMonth(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            return monthNames[date.getMonth()]
        }
    }
}
</script>

<style scoped>
.blog-card.style-one {
    transition: transform 0.3s ease;
}

.blog-card.style-one:hover {
    transform: translateY(-5px);
}

.blog-img {
    position: relative;
}

.blog-img img {
    width: 100%;
    height: 280px;
    object-fit: cover;
    transition: transform 0.5s ease;
}

.img-hover-scale:hover .img-scale img {
    transform: scale(1.1);
}

.blog-date {
    top: 20px;
    left: 20px;
    width: 60px;
    height: 70px;
    padding: 10px;
    line-height: 1.2;
    font-size: 14px;
}

.blog-date span {
    font-size: 24px;
    margin-bottom: 2px;
}

.blog-info {
    padding: 25px;
    margin-top: -10px;
    position: relative;
    z-index: 2;
}

.blog-metainfo {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid #e5e5e5;
}

.blog-metainfo li {
    font-size: 14px;
    color: #666;
}

.blog-metainfo li a {
    color: #666;
    text-decoration: none;
    transition: color 0.3s ease;
}

.blog-metainfo li a:hover {
    color: var(--bs-primary);
}

.blog-metainfo li:not(:last-child):after {
    content: '';
    position: absolute;
    right: -10px;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    background: #999;
    border-radius: 50%;
}

.blog-info h3 a {
    text-decoration: none;
}

@media (max-width: 991px) {
    .blog-img img {
        height: 250px;
    }
}

@media (max-width: 767px) {
    .blog-img img {
        height: 220px;
    }
    
    .blog-info {
        padding: 20px;
    }
    
    .blog-date {
        width: 50px;
        height: 60px;
        padding: 8px;
        font-size: 12px;
    }
    
    .blog-date span {
        font-size: 20px;
    }
}
</style>
